// Common utility functions and constants for charts
import React, { useMemo } from 'react';
import { TooltipProps } from 'recharts';


// If you have other exports in your ChartUtils.js file, include them here
export const COLORS = [
  '#4285F4', // Blue
  // '#34A853', // Green - reserved for Min Limit
  '#F39C12', // Amber (more visible than yellow)
  // '#EA4335', // Red - reserved for Max Limit
  '#8758FF', // Purple
  '#00C9A7', // Teal
  '#FF6D28', // Orange
  '#FF1E56', // Pink
  '#1ABC9C', // Turquoise
  '#9B59B6', // Violet
  '#3498DB', // Lighter Blue
  '#D35400', // Burnt Orange
  '#2ECC71', // Emerald
  '#16A085', // Green Sea
  '#2980B9', // Strong Blue
  '#8E44AD', // Dark Purple
  '#E74C3C', // Bright Red
  '#27AE60', // Nephritis
  '#2C3E50', // Midnight Blue
  '#F1C40F'  // Darker Yellow/Gold
];

export const formatCents = (value) => `${Number(value).toFixed(2)} c`;

export const formatEuros = (value) => `${(Number(value) / 100).toFixed(2)} €`;

export const formatCelcius = (value) => `${Number(value).toFixed(1)}  \u00b0C`;

export const formatKWh = (value, decimals = 1) => {
  if (value === null || value === undefined) return '-';
  
  const num = Number(value);
  
  // Handle different scales
  if (num >= 1000) {
    decimals = 0
    // Convert to MWh for larger values
  //  return `${num.toFixed(decimals)} kWh${(num / 1000).toFixed(decimals)} MWh`;
  } 
  
  return `${num.toFixed(decimals)} kWh`;
};

// Alternative approach: Show all non-zero values at current x-position
export const CustomStackedBarTooltip = ({ 
  active, 
  payload, 
  label,
  formatter = (value) => `${value}`
}) => {
  if (!active || !payload || !payload.length) return null;
  
  // Filter to only show non-zero segments
  const visibleSegments = payload.filter(entry => entry.value > 0);
  
  // If no visible segments, don't show tooltip
  if (visibleSegments.length === 0) return null;
  
  return (
    <div style={{
      backgroundColor: 'white',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      maxWidth: '200px'
    }}>
      <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '5px' }}>{`Date: ${label}`}</p>
      
      {visibleSegments.map((entry, index) => (
        <p key={`item-${index}`} style={{ 
          margin: '3px 0', 
          color: entry.color,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span style={{ marginRight: '10px' }}>{entry.name}:</span>
          <span style={{ fontWeight: 'bold' }}>
            {typeof formatter === 'function' ? formatter(entry.value) : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
};

// Updated CustomLegend component with matching line and text colors
// This would typically be in your ChartUtils.js file


export const CustomLegend = ({ 
  payload, 
  colors, 
  colorMap,
  activeData, 
  containerWidth, 
  processedData,
  valueFormatter = (value) => `${value?.toFixed(1) || '0.0'}`
}) => {
  const getOptimalColumns = (width) => {
    if (width < 500) return 1;
    if (width < 800) return 2;
    if (width < 1200) return 3;
    return 4;
  };
  const columns = getOptimalColumns(containerWidth);
  
  // Prioritize colorMap for color determination
  const getColorForEntry = (entry, index) => {
    if (colorMap && entry.dataKey && colorMap[entry.dataKey]) {
      return colorMap[entry.dataKey];
    }
    
    if (colors && colors[index]) {
      return colors[index];
    }
    
    return '#888'; // Default fallback color
  };
  
  // Create a robust color mapping system
  const getColorForDataKey = useMemo(() => {
    const mappedColors = {};
    
    if (payload && payload.length > 0) {
      payload.forEach((entry, index) => {
        if (entry && entry.dataKey) {
          mappedColors[entry.dataKey] = getColorForEntry(entry, index);
        }
      });
    }
    
    return (dataKey) => mappedColors[dataKey] || '#888';
  }, [payload, colors, colorMap]);
  
  // Organize the payload with specific order preferences
  // This ensures Min Limit and Max Limit appear at the end
  const orderedPayload = useMemo(() => {
    if (!payload || payload.length === 0) return [];
    
    // Create categories for different types of entries
    const limitEntries = [];
    const averageEntries = [];
    const sensorEntries = [];
    
    // Categorize each entry
    payload.forEach((entry) => {
      if (!entry || !entry.dataKey) return;
      
      const key = entry.dataKey;
      const name = entry.name || key;
      
      // Check if it's a limit entry
      if (name === 'Min Limit' || name === 'Max Limit' || 
          key === 'min_limit' || key === 'max_limit' ||
          name.includes('Limit') || key.includes('limit')) {
        limitEntries.push(entry);
      }
      // Check if it's an average entry
      else if (name === 'Average' || key === 'Average' || 
               name === 'average' || key === 'average') {
        averageEntries.push(entry);
      }
      // Otherwise, it's a sensor entry
      else {
        sensorEntries.push(entry);
      }
    });
    
    // Sort the limit entries to ensure Max Limit comes before Min Limit
    limitEntries.sort((a, b) => {
      const aName = a.name || a.dataKey;
      const bName = b.name || b.dataKey;
      
      // Max Limit should come before Min Limit
      if (aName.includes('Max') && bName.includes('Min')) return -1;
      if (aName.includes('Min') && bName.includes('Max')) return 1;
      
      return 0;
    });
    
    // Combine all entries in the desired order: sensors, then averages, then limits
    return [...sensorEntries, ...averageEntries, ...limitEntries];
  }, [payload]);
  
  // If we have no data, don't render
  if (!activeData || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div style={{ 
      width: containerWidth,
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: '8px',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    }}>
      {orderedPayload.map((entry, index) => {
        // Check for valid data
        if (!entry || !entry.dataKey) return null;
        
        // Get current value safely
        let currentValue = 0;
        if (activeData && entry.dataKey in activeData) {
          currentValue = activeData[entry.dataKey];
        }
        
        // Get color for this entry
        const color = getColorForEntry(entry, index);
        
        return (
          <div 
            key={entry.dataKey}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              cursor: 'default',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              flex: 1
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                flexShrink: 0
              }} />
              <span style={{
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: color // Match the text color to the line color
              }} title={entry.name || entry.dataKey}>
                {entry.name || entry.dataKey}
              </span>
            </div>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              marginLeft: '8px',
              flexShrink: 0,
              color: color // Match the value text color to the line color
            }}>
              {valueFormatter(currentValue, entry.dataKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};


export const CustomLegend_old = ({ 
  payload, 
  colors, 
  colorMap, // Add colorMap as a prop
  activeData, 
  containerWidth, 
  processedData,
  valueFormatter = (value) => `${value?.toFixed(1) || '0.0'}`
}) => {
  const getOptimalColumns = (width) => {
    if (width < 500) return 1;
    if (width < 800) return 2;
    if (width < 1200) return 3;
    return 4;
  };
  const columns = getOptimalColumns(containerWidth);
  
  // Create a robust color mapping system
  const getColorForEntry = useMemo(() => {
    // First priority: Use provided colorMap directly
    if (colorMap && typeof colorMap === 'object') {
      return (entry) => colorMap[entry.dataKey] || null;
    }
    
    // Second priority: Create a mapping from payload to colors array
    if (payload && colors && colors.length > 0) {
      const mappedColors = {};
      payload.forEach((entry, index) => {
        if (entry && entry.dataKey) {
          mappedColors[entry.dataKey] = colors[index % colors.length];
        }
      });
      return (entry) => mappedColors[entry.dataKey] || null;
    }
    
    // Fallback: return null
    return () => null;
  }, [colorMap, payload, colors]);
  
  // Sort sensors by total consumption, but memoize to prevent recalculation
  const sortedPayload = useMemo(() => {
    if (!processedData || !payload || payload.length === 0) return [];
    
    return [...payload].sort((a, b) => {
      // Make sure dataKey exists before calculating
      if (!a.dataKey || !b.dataKey) return 0;
      
      const aTotal = processedData.reduce((sum, entry) => {
        // Handle undefined values safely
        const value = entry[a.dataKey];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
      
      const bTotal = processedData.reduce((sum, entry) => {
        // Handle undefined values safely
        const value = entry[b.dataKey];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
      
      return bTotal - aTotal;
    });
  }, [payload, processedData]);
  
  // If we have no data, don't render
  if (!activeData || !payload || payload.length === 0) {
    return null;
  }

  // For debugging - log keys and colors
  console.log('Legend keys and colors:');
  sortedPayload.forEach(entry => {
    if (entry && entry.dataKey) {
      const entryColor = getColorForEntry(entry) || '#888';
      console.log(`${entry.dataKey}: ${entryColor}`);
    }
  });

  return (
    <div style={{ 
      width: containerWidth,
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: '8px',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    }}>
      {sortedPayload.map((entry, index) => {
        // Check for valid data
        if (!entry || !entry.dataKey) return null;
        
        // Get color from our memoized color mapping function
        // This ensures color consistency between chart and legend
        const color = getColorForEntry(entry) || 
                     (colors && colors[index] ? colors[index] : '#888');
        
        // Get current value safely
        let currentValue = 0;
        if (activeData && entry.dataKey in activeData) {
          currentValue = activeData[entry.dataKey];
        }
        
        return (
          <div 
            key={entry.dataKey}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              cursor: 'default',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              flex: 1
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                flexShrink: 0
              }} />
              <span style={{
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: color // Match the text color to the line color
              }} title={entry.name || entry.dataKey}>
                {entry.name || entry.dataKey}
              </span>
            </div>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              marginLeft: '8px',
              flexShrink: 0,
              color: color // Match the value text color to the line color
            }}>
              {valueFormatter(currentValue, entry.dataKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

